import { useMemo } from 'react';
import { FUND_TYPES } from './get-fund-type';
import {
  useProfilePlanHoldingsBulk,
  usePortfolioHoldings
} from '../../hooks/api';
import get from 'lodash/get';
import { getInvestmentChangeModes } from '../../components/molecules/profile/dynamic-greeting/dynamic-greeting-helper';

export const INVEST_BUTTON_MODES = {
  modal: 'modal',
  link: 'link',
  hide: 'hide'
};

export const DYNAMIC_GREETING_MODES = {
  available: 'available',
  closing: 'closing',
  held: 'held',
  hide: 'hide',
  multipleChanges: 'multipleChanges',
  notAvailable: 'notAvailable',
  opening: 'opening',
  restricted: 'restricted'
};

const getInvestButtonMode = ({
  channel,
  fundType,
  planHoldingsList,
  xid
}) => {
  if (
    fundType === FUND_TYPES.retirePlus ||
    fundType === FUND_TYPES.retirePlusPro ||
    fundType === FUND_TYPES.customPortfolioModel ||
    fundType === FUND_TYPES.fixedAnnuityTiaaQdia // TIAA-10747 Remove invest button
  ) {
    return INVEST_BUTTON_MODES.hide;
  }
  if (channel === 'public') {
    return INVEST_BUTTON_MODES.modal;
  }
  if (channel === 'mytiaa' || channel === 'ifa') {
    const includesCurrentFund = planHoldingsList.some(
      planHoldings =>
        planHoldings.data &&
        planHoldings.data.holdings &&
        planHoldings.data.holdings.some(holding => holding.xid === xid)
    );
    if (includesCurrentFund) {
      return INVEST_BUTTON_MODES.link;
    }
  }
  return INVEST_BUTTON_MODES.hide;
};

const removeEmptyArray = ({ holdings }) => {
  return (holdings.filter(e => {
    return e.length;
  }));
};

const getDynamicGreetingData = ({
  channel,
  planHoldingsList,
  returnMode,
  symbol,
  participantsHoldingData,
  xid
}) => {
  const greetingModes = [DYNAMIC_GREETING_MODES.hide];
  let holdingsHeld = [];
  const holdingsForGreeting = {
    holdingsHeld: [],
    holdingsAvailable: []
  };
  const extractedHoldings = [];
  if (channel === 'mytiaa' || channel === 'ifa') {
    greetingModes.pop();
    const xidAsString = xid ? xid.toString() : '';
    // Dynamic Greeting: Filters current fund from funds held by user got from participants holdings API response.
    const filteredParticipantHoldings = participantsHoldingData && participantsHoldingData.filter(participantHoldings =>
      (participantHoldings.tickers.includes(xidAsString) || participantHoldings.tickers.includes(symbol)));
    planHoldingsList.forEach(holding => {
      if (holding.data && holding.data.holdings) {
        extractedHoldings.push(holding.data.holdings);
      }
    });

    if (filteredParticipantHoldings && filteredParticipantHoldings.length) {
      greetingModes.push(DYNAMIC_GREETING_MODES.held);
    }
    // Dynamic Greeting: Extracting just the plan numbers from participants holdings to compute held and availability scenarios.
    const participantPlanNumbers = filteredParticipantHoldings.length
      ? filteredParticipantHoldings
        .map(({ planNumber } = {}) => {
          return planNumber;
        })
        .filter(Boolean)
      : [];
    if (greetingModes[0] === DYNAMIC_GREETING_MODES.held) {
      holdingsHeld = extractedHoldings.map(holdings => holdings.filter(holding =>
        (holding.xid === xid || holding.ticker === symbol) && participantPlanNumbers.includes(holding.planNumber)));
    }
    // Dynamic Greeting: Available funds will include the funds held by the user also.
    const holdingsAvailable = extractedHoldings.map(holdings => holdings.filter(holding =>
      (holding.xid === xid || holding.ticker === symbol)));
    holdingsForGreeting.holdingsAvailable = removeEmptyArray({ holdings: holdingsAvailable });
    holdingsForGreeting.holdingsHeld = removeEmptyArray({ holdings: holdingsHeld });
    if (!returnMode) {
      return holdingsForGreeting;
    }
    if (holdingsForGreeting.holdingsAvailable.length) {
      greetingModes.push(DYNAMIC_GREETING_MODES.available);
      // Dynamic Greeting: Computing mode for investment changing scenarios.
      const { investmentChangingMode } = getInvestmentChangeModes({ holdingsIncludingCurrentFund: holdingsForGreeting.holdingsAvailable });
      if (investmentChangingMode) {
        greetingModes.push(investmentChangingMode);
      }
    } else if (!greetingModes.length) {
      greetingModes.push(DYNAMIC_GREETING_MODES.notAvailable);
    }
  }
  if (returnMode) {
    return greetingModes;
  }
  return [];

};

const useParticipantHoldingsStore = ({ apiConfigBase, commonState }) => {

  const { channel, fundType, xref } = commonState;

  const xid = get(xref, 'data.xid', null);
  const symbol = get(xref, 'data.symbol', null);

  const isATA = fundType === FUND_TYPES.afterTaxAnnuity;

  const holdings = usePortfolioHoldings({ channel, shouldLoad: !isATA });

  const planHoldingParams = holdings && holdings.data && holdings.data.length
    ? holdings.data
      .map(({ planNumber } = {}) => {
        return planNumber ? { planNumber } : false;
      })
      .filter(Boolean)
    : [];

  const participantsHoldingData = holdings && holdings.data && holdings.data.length
    ? holdings.data : [];

  const planHoldings = useProfilePlanHoldingsBulk({
    ...apiConfigBase,
    params: planHoldingParams
  });

  const planHoldingsData = useMemo(() => { return planHoldings && planHoldings.isLoaded ? planHoldings.data : []; }, [planHoldings]);

  const investButtonMode = useMemo(() => getInvestButtonMode({
    channel,
    fundType,
    planHoldingsList: planHoldingsData,
    xid
  }), [channel, fundType, planHoldingsData, xid]);

  const dynamicGreetingMode = useMemo(() => getDynamicGreetingData({
    channel,
    planHoldingsList: planHoldingsData,
    returnMode: true,
    symbol,
    participantsHoldingData,
    xid
  }), [channel, planHoldingsData, symbol, participantsHoldingData, xid]);

  const holdingsIncludingCurrentFund = useMemo(() => getDynamicGreetingData({
    channel,
    planHoldingsList: planHoldingsData,
    symbol,
    participantsHoldingData,
    xid
  }), [channel, planHoldingsData, symbol, participantsHoldingData, xid]);

  return {
    isLoaded: planHoldings && planHoldings.isLoaded,
    data: {
      dynamicGreetingMode,
      holdingsIncludingCurrentFund,
      investButtonMode
    }
  };
};

export default useParticipantHoldingsStore;
